import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOffline } from '../contexts/OfflineContext';
import { useShift } from '../contexts/ShiftContext';
import { Check, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Asistencia() {
  const { user, checkAuth } = useAuth();
  const { isOnline, addOfflineItem, getCachedData } = useOffline();
  const { refresh: refreshShift } = useShift();
  const navigate = useNavigate();

  const tieneAsignacion = !!user?.punto_venta_id;

  const [puntosVenta, setPuntosVenta] = useState([]);
  const [selectedPunto, setSelectedPunto] = useState(tieneAsignacion ? user.punto_venta_id : null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [asistRes, puntosRes] = await Promise.allSettled([
        axios.get(`${API}/asistencia/hoy`),
        axios.get(`${API}/puntos-venta`),
      ]);

      if (puntosRes.status === 'fulfilled') {
        setPuntosVenta(puntosRes.value.data.sort((a, b) => a.name.localeCompare(b.name, 'es')));
      } else {
        const cached = getCachedData('puntos-venta');
        if (cached) setPuntosVenta([...cached].sort((a, b) => a.name.localeCompare(b.name, 'es')));
      }

      const asistencia = asistRes.status === 'fulfilled' ? asistRes.value.data : null;
      if (asistencia) {
        // Si ya tiene asistencia con turno abierto, ir directo al inventario
        try {
          const { data: corte } = await axios.get(`${API}/corte/hoy/${asistencia.punto_venta_id}`);
          if (!corte) {
            navigate('/inventario-inicial', { replace: true });
            return;
          }
        } catch {
          navigate('/inventario-inicial', { replace: true });
          return;
        }
        if (!tieneAsignacion) setSelectedPunto(asistencia.punto_venta_id);
      }
    } catch {
      const cached = getCachedData('puntos-venta');
      if (cached) setPuntosVenta(cached);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedPunto) {
      setError('Selecciona un punto de venta');
      return;
    }

    setError('');
    setSubmitting(true);

    if (isOnline) {
      try {
        await axios.post(`${API}/asistencia`, { punto_venta_id: selectedPunto });
        await checkAuth();
        await refreshShift();
        navigate('/inventario-inicial', { replace: true });
      } catch (error) {
        setError(error.response?.data?.detail || 'Error al registrar asistencia');
        setSubmitting(false);
      }
    } else {
      addOfflineItem('asistencias', {
        punto_venta_id: selectedPunto,
        fecha: new Date().toISOString().split('T')[0],
        hora_entrada: new Date().toISOString(),
        user_name: user.name,
      });
      navigate('/inventario-inicial', { replace: true });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  const puntoNombre = puntosVenta.find(p => p.id === selectedPunto)?.name || '';

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6" data-testid="asistencia-page">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-2xl font-bold text-zinc-900">Registrar asistencia</h1>
          <p className="text-zinc-500 mt-1 text-sm">
            {new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}

        {tieneAsignacion ? (
          /* Vendedor con punto asignado: solo mostrar el punto y confirmar */
          <div className="mb-8">
            <p className="text-xs font-medium text-zinc-500 tracking-wide mb-3 uppercase">Tu punto de venta</p>
            <div className="flex items-center gap-3 p-4 bg-zinc-50 border-2 border-zinc-200 rounded-xl">
              <div className="w-10 h-10 bg-zinc-900 rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-zinc-900">{puntoNombre}</span>
            </div>
          </div>
        ) : (
          /* Sin asignación fija: mostrar selector */
          <div className="mb-8">
            <p className="text-xs font-medium text-zinc-500 tracking-wide mb-3 uppercase">Punto de venta</p>
            <select
              value={selectedPunto || ''}
              onChange={e => setSelectedPunto(e.target.value || null)}
              className="w-full h-14 px-4 text-lg border-2 border-zinc-200 rounded-xl bg-white text-zinc-900 focus:outline-none focus:border-zinc-900"
              data-testid="select-punto-venta"
            >
              <option value="">Selecciona un punto de venta</option>
              {puntosVenta.map(punto => (
                <option key={punto.id} value={punto.id}>{punto.name}</option>
              ))}
            </select>
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={!selectedPunto || submitting}
          className="w-full h-16 bg-zinc-900 text-white rounded-xl text-lg font-bold flex items-center justify-center gap-2 active:bg-zinc-700 disabled:opacity-50"
          data-testid="btn-confirmar-asistencia"
        >
          {submitting ? (
            <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Check className="w-6 h-6" />
              Confirmar asistencia
            </>
          )}
        </button>
      </div>
    </div>
  );
}
